import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChange, signInUser, signOutUser, getCurrentUserToken } from '../utils/firebase';
import { User, AuthState, UserRole } from '../types';
import { clearUserDataCache, apiClient } from '../utils/api';
import { clearImpersonationReturn } from '../utils/impersonation';
import { useQueryClient } from '@tanstack/react-query';
import {
  AgencyMembership,
  clearAgencyScopedStorage,
  getActiveMembershipId,
  setActiveMembershipId,
} from '../utils/activeMembership';
import { AgencyPickerModal } from '../components/AgencySwitcher';
import { auth } from '../utils/firebase';

/** /users/me: several agencies, none remembered → the picker decides. */
class AgencySelectionRequired extends Error {
  constructor(public memberships: AgencyMembership[]) {
    super('agency selection required');
  }
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  /**
   * Opens another agency of the same login. Full reload on purpose: every
   * cache (react-query, module caches, drafts) of the old agency is dropped.
   */
  switchAgency: (membershipId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const queryClient = useQueryClient();
  const [previousUserEmail, setPreviousUserEmail] = useState<string | null>(null);
  const [agencyChoices, setAgencyChoices] = useState<AgencyMembership[] | null>(null);

  // Real function to get user details from backend with permissions
  const getUserDetails = async (firebaseUser: FirebaseUser): Promise<User> => {
    try {
      // Try to fetch the user data from backend with permissions first
      const response = await apiClient.get('/users/me');
      const backendUser = response.data.data;

      if (backendUser?.needsAgencySelection) {
        throw new AgencySelectionRequired(backendUser.memberships || []);
      }

      console.log('✅ Fetched user data from backend with permissions:', backendUser);

      // Convert backend user to frontend User type
      const user: User = {
        id: backendUser.id,
        email: backendUser.email,
        firstName: backendUser.firstName,
        lastName: backendUser.lastName,
        role: backendUser.role as UserRole,
        agencyId: backendUser.agencyId,
        isActive: backendUser.isActive,
        permissions: backendUser.permissions,
        createdAt: backendUser.createdAt,
        updatedAt: backendUser.updatedAt,
        memberships: backendUser.memberships || [],
      };

      return user;
    } catch (error: any) {
      if (error instanceof AgencySelectionRequired) throw error;
      console.error('❌ CRITICAL: Failed to fetch user data from backend:', error);

      // The backend refuses deactivated accounts (403) — show its message
      // instead of the generic configuration error.
      if (error?.response?.status === 403 && error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // SECURITY: Only allow super admin fallback for specific authorized email
      if (firebaseUser.email === 'admin@example.com') {
        console.warn('⚠️ Using super admin fallback for admin@example.com');
        return {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          firstName: 'Super',
          lastName: 'Admin',
          role: UserRole.SUPER_ADMIN,
          isActive: true,
          permissions: {
            dashboard: true,
            patients: true,
            caregivers: true,
            matching: true,
            shifts: true,
            invoices: true,
            emails: true,
            templates: true,
            tasks: true,
            knowledge: true,
            files: true,
            staff: true,
            settings: true,
            billing: true,
            agencies: true,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      }

      // SECURITY: Do NOT provide fallback for other users to prevent agency isolation breach
      console.error('❌ User not found in backend database:', firebaseUser.email);
      console.error('❌ Refusing authentication to prevent data isolation breach');
      throw new Error('User account not properly configured. Please contact administrator.');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      console.log('🔥 Firebase auth state changed:', firebaseUser ? firebaseUser.email : 'signed out');

      if (firebaseUser) {
        try {
          // Check if this is a different user logging in
          const currentUserEmail = firebaseUser.email;
          const isDifferentUser = previousUserEmail && previousUserEmail !== currentUserEmail;

          if (isDifferentUser) {
            console.log('🔄 Different user detected, clearing all caches...');
            clearUserDataCache();
            queryClient.clear();
            // Clear agency theme color when switching users
            localStorage.removeItem('agencyThemeColor');
          } else if (!previousUserEmail) {
            // Only clear cache on initial login, not on every auth state change
            console.log('🧹 Clearing cache for initial user login...');
            clearUserDataCache();
            queryClient.clear();
          }

          const user = await getUserDetails(firebaseUser);
          console.log('✅ User details loaded successfully:', user);

          // Update previous user email
          setPreviousUserEmail(currentUserEmail || null);

          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          if (error instanceof AgencySelectionRequired) {
            setAgencyChoices(error.memberships);
            setAuthState({ user: null, isAuthenticated: false, isLoading: false, error: null });
            return;
          }
          console.error('❌ Failed to load user details:', error);
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            // Show the backend's reason (e.g. "Ihr Zugang wurde deaktiviert").
            error: (error as any)?.message || 'Failed to load user details',
          });
        }
      } else {
        console.log('👤 User signed out');
        setPreviousUserEmail(null);
        setAgencyChoices(null);
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, [queryClient, previousUserEmail]);

  const login = async (email: string, password: string, captchaToken?: string): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      // Note: In a production environment, the captchaToken should be sent to the backend
      // for server-side verification with Google's reCAPTCHA API
      if (captchaToken) {
        console.log('🔒 CAPTCHA token received:', captchaToken.substring(0, 20) + '...');
        // Here you would typically send the captchaToken to your backend for verification
        // await apiClient.post('/auth/verify-captcha', { token: captchaToken });
      } else {
        console.log('🔓 CAPTCHA disabled for local development');
      }

      await signInUser(email, password);
      // User state will be updated by the auth state change listener
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Login failed',
      }));
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      await signOutUser();

      // Clear all caches to prevent stale data
      console.log('🧹 Clearing all caches on logout...');

      // Clear OTP user data
      localStorage.removeItem('userData');

      // Clear agency theme color
      localStorage.removeItem('agencyThemeColor');

      // Clear user data cache
      clearUserDataCache();

      // Clear stashed impersonation return token, if any
      clearImpersonationReturn();

      // Clear all React Query cache
      queryClient.clear();
      
      // Reset auth state
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      
      console.log('✅ All caches cleared successfully');
    } catch (error: any) {
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.message || 'Logout failed',
      }));
      throw error;
    }
  };

  const getToken = async (): Promise<string | null> => {
    return await getCurrentUserToken();
  };

  const switchAgency = (membershipId: string) => {
    const email = auth.currentUser?.email || authState.user?.email;
    if (!email) return;
    if (membershipId === getActiveMembershipId(email) && authState.user?.id === membershipId) return;
    setActiveMembershipId(email, membershipId);
    clearUserDataCache();
    clearAgencyScopedStorage();
    queryClient.clear();
    window.location.assign('/');
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    getToken,
    switchAgency,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <AgencyPickerModal
        open={!!agencyChoices?.length}
        memberships={agencyChoices || []}
        onSelect={(m) => switchAgency(m.id)}
        onLogout={() => { setAgencyChoices(null); void logout(); }}
      />
    </AuthContext.Provider>
  );
};

/**
 * Public feature showcase: the real feature pages run for a visitor who is not
 * signed in, so they get an invented demo admin. Every API call is answered by
 * demo mode in the browser — this user never reaches the server.
 */
export const ShowcaseAuthProvider: React.FC<{ user: User; children: ReactNode }> = ({ user, children }) => {
  const value: AuthContextType = {
    user,
    isAuthenticated: true,
    isLoading: false,
    error: null,
    login: async () => {},
    logout: async () => {},
    getToken: async () => null,
    switchAgency: () => {},
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
